import { Link } from "react-router-dom";
import type { Topic } from "@/types/content";
import { formatHeat } from "@/utils/format";

type TopicCardProps = {
  topic: Topic;
  rank?: number;
};

export default function TopicCard({ topic, rank }: TopicCardProps) {
  return (
    <Link
      to={`/topics/${topic.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200/80 bg-background-100 transition-all duration-300 hover:-translate-y-1 hover:border-primary-500/60"
    >
      <div className="relative h-44 w-full overflow-hidden">
        <img
          src={topic.cover}
          alt={`${topic.title} - 深夜吃瓜话题封面`}
          title={`${topic.title} - 深夜吃瓜话题讨论`}
          className="h-44 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-background-950/20 to-transparent" />
        <span className="absolute left-3 top-3 rounded-full bg-background-950/80 px-2.5 py-1 text-xs font-medium text-accent-500 backdrop-blur">
          {topic.categoryName}
        </span>
        {typeof rank === "number" && (
          <span className="brand-font absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-base text-background-50 dark:text-foreground-950">
            {rank}
          </span>
        )}
        <div className="absolute bottom-3 left-3 flex items-center gap-3 text-xs text-foreground-200">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-400" />
            {formatHeat(topic.heat)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line text-secondary-400" />
            {topic.comments}
          </span>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="heading-serif text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-500 md:text-[17px]">
          {topic.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-500">
          {topic.summary}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {topic.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-secondary-100/80 px-2 py-0.5 text-xs text-secondary-900"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-background-200/70 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-3-line" />
            {topic.author}
          </span>
          <span>{topic.time}</span>
        </div>
      </div>
    </Link>
  );
}